import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plane, Plus, Search, X, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, ClipboardList,
  History, Trash2, Archive, ArchiveRestore, ArrowLeft, LayoutList, PanelLeftClose, PanelLeftOpen,
  Pencil, QrCode, FileWarning, MessageSquare, Clock, Square, Send, Star, Paperclip, Timer, UserPlus, Stethoscope, PlaneTakeoff,
} from "lucide-react";
import QRCode from "qrcode";
import { EmployeeSearchSelect } from "@/components/EmployeeSearchSelect";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const MEL_CATEGORIES = ["A", "B", "C", "D"];
const AC_STATUS = {
  normal: { label: "Serviceable", cls: "bg-emerald-100 text-emerald-800", plane: "text-emerald-500" },
  heavy_check: { label: "Heavy Check", cls: "bg-blue-100 text-blue-800", plane: "text-blue-500" },
  mel: { label: "MEL", cls: "bg-amber-100 text-amber-800", plane: "text-amber-500" },
  capex: { label: "CAPEX", cls: "bg-rose-100 text-rose-800", plane: "text-rose-500" },
  unserviceable: { label: "Unserviceable", cls: "bg-red-100 text-red-800", plane: "text-red-600" },
  offline: { label: "Offline", cls: "bg-gray-200 text-gray-600", plane: "text-gray-400" },
};
const PRIORITY_CLS = {
  Urgent: "bg-rose-100 text-rose-800",
  High: "bg-amber-100 text-amber-800",
  Medium: "bg-sky-100 text-sky-800",
  Low: "bg-gray-100 text-gray-600",
};
const DUE_SOON = { hours: 10, cycles: 25, days: 7 };

const todayIso = () => new Date().toLocaleDateString("en-CA");
const isOverdue = (d) => !!d && d < todayIso();
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-CA");
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const getGps = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error("unsupported")); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => reject(new Error("denied")),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });

// Auto-flag jobs coming due against the aircraft's live hours/cycles/date.
function dueFlag(wo, ac) {
  let level = null;
  const parts = [];
  const mark = (l) => { if (l === "overdue" || level === null) level = l === "overdue" ? "overdue" : (level || "soon"); };
  if (wo.due_date) {
    if (wo.due_date < todayIso()) { mark("overdue"); parts.push("date passed"); }
    else if (wo.due_date <= addDays(DUE_SOON.days)) { mark("soon"); parts.push(`due ${wo.due_date}`); }
  }
  if (wo.due_hours != null && ac.airframe_hours != null) {
    const rem = Math.round((wo.due_hours - ac.airframe_hours) * 10) / 10;
    if (rem <= 0) { mark("overdue"); parts.push(`${Math.abs(rem)} hrs over`); }
    else if (rem <= DUE_SOON.hours) { mark("soon"); parts.push(`${rem} hrs left`); }
  }
  if (wo.due_cycles != null && ac.cycles != null) {
    const rem = wo.due_cycles - ac.cycles;
    if (rem <= 0) { mark("overdue"); parts.push(`${Math.abs(rem)} cyc over`); }
    else if (rem <= DUE_SOON.cycles) { mark("soon"); parts.push(`${rem} cyc left`); }
  }
  if (!level) return null;
  return {
    level,
    label: level === "overdue" ? "OVERDUE" : "DUE SOON",
    detail: parts.join(" · "),
    cls: level === "overdue" ? "bg-rose-600 text-white" : "bg-amber-400 text-amber-950",
  };
}

async function printAircraftQr(ac) {
  try {
    const { data } = await api.get(`/maintenance/aircraft/${encodeURIComponent(ac.tail)}/qr-pin`);
    const url = `${window.location.origin}/maintenance/scan/${encodeURIComponent(ac.tail)}`;
    const dataUrl = await QRCode.toDataURL(url, { width: 512, margin: 2 });
    const esc = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const w = window.open("", "_blank", "width=480,height=680");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print the QR code"); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>QR — ${esc(ac.tail)}</title>
<style>body{font-family:Arial,sans-serif;text-align:center;padding:24px}img{width:280px;height:280px}h1{font-size:18px;margin:8px 0 2px}p{font-size:12px;color:#555;margin:2px 0}.pin{font-size:22px;font-weight:bold;letter-spacing:6px;color:#111;margin-top:10px;border:2px solid #111;display:inline-block;padding:4px 14px;border-radius:6px}</style></head>
<body><img src="${dataUrl}" alt="QR code"/><h1>${esc(ac.tail)}</h1>
<p>${esc(ac.fleet || "")}</p>
<p>Scan to view status &amp; report a problem</p>
<div class="pin">PIN ${esc(data.pin)}</div>
<p style="margin-top:6px">Use this PIN when reporting a problem</p>
<script>window.onload=function(){window.print();}</${"script"}></body></html>`);
    w.document.close();
  } catch {
    toast.error("Failed to generate QR code");
  }
}

function ColumnHead({ children }) {
  return <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">{children}</div>;
}

function WOToleranceDialog({ wo, onClose, onApplied }) {
  const [vals, setVals] = useState({ hours: "", cycles: "", days: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (wo) setVals({ hours: "", cycles: "", days: "" }); }, [wo]);

  const apply = async () => {
    const payload = {
      hours: vals.hours === "" ? null : Number(vals.hours),
      cycles: vals.cycles === "" ? null : Number(vals.cycles),
      days: vals.days === "" ? null : Number(vals.days),
    };
    if (!payload.hours && !payload.cycles && !payload.days) { toast.error("Enter at least one tolerance value"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/work-orders/${wo.id}/tolerance`, payload);
      toast.success("Tolerance applied — due fields extended");
      onClose();
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply tolerance");
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, step) => (
    <div>
      <Label className="text-xs uppercase tracking-widest text-gray-500">{label}</Label>
      <Input className="mt-1" type="number" step={step} placeholder="0" value={vals[key]} onChange={(e) => setVals((s) => ({ ...s, [key]: e.target.value }))} data-testid={`maint-tol-${key}`} />
    </div>
  );

  return (
    <Dialog open={!!wo} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="maint-tol-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Timer className="w-4 h-4" /> Apply tolerance</DialogTitle>
          <DialogDescription className="whitespace-pre-wrap">{wo?.title?.slice(0, 120)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {field("hours", "Hours", "0.1")}
          {field("cycles", "Cycles", "1")}
          {field("days", "Days", "1")}
        </div>
        <p className="text-[10px] text-gray-500" data-testid="maint-tol-hint">
          Each value is added to the job&apos;s current dues — hours → due at hours{wo?.due_hours != null ? ` (${wo.due_hours})` : ""}, cycles → due at landings{wo?.due_cycles != null ? ` (${wo.due_cycles})` : ""}, days → due date{wo?.due_date ? ` (${wo.due_date})` : ""}. Toggle off to revert.
        </p>
        <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white" onClick={apply} disabled={busy} data-testid="maint-tol-apply">
          Apply tolerance
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrders({ ac, orders, members, user, onSaved }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", priority: "Medium", due_date: "", assignee_id: "", affected_area: "", due_hours: "", due_cycles: "" });
  const [completing, setCompleting] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [assignId, setAssignId] = useState("");
  const [assignList, setAssignList] = useState([]);
  const [tolWO, setTolWO] = useState(null);
  const [doneForm, setDoneForm] = useState({ notes: "", parts: "", labor_hours: "" });
  const [usPrompt, setUsPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [commentText, setCommentText] = useState({});
  // Sorted by urgency: overdue → due soon → rest; ties by priority, then oldest.
  const open = orders
    .filter((o) => o.status === "open")
    .sort((a, b) => {
      const lv = (wo) => { const f = dueFlag(wo, ac); return f ? (f.level === "overdue" ? 0 : 1) : 2; };
      const pr = (wo) => { const i = PRIORITIES.indexOf(wo.priority); return i === -1 ? 2 : PRIORITIES.length - 1 - i; };
      return lv(a) - lv(b) || pr(a) - pr(b) || (a.created_at || "").localeCompare(b.created_at || "");
    });

  const addComment = async (wo) => {
    const t = (commentText[wo.id] || "").trim();
    if (!t) { toast.error("Write a comment first"); return; }
    try {
      await api.post(`/maintenance/work-orders/${wo.id}/comments`, { text: t });
      setCommentText((m) => ({ ...m, [wo.id]: "" }));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add comment");
    }
  };

  const create = async () => {
    if (!form.title.trim()) { toast.error("Describe the work needed"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/aircraft/${ac.tail}/work-orders`, {
        title: form.title,
        priority: form.priority,
        due_date: form.due_date || null,
        assignee_id: form.assignee_id || null,
        affected_area: form.affected_area || null,
        due_hours: form.due_hours === "" ? null : Number(form.due_hours),
        due_cycles: form.due_cycles === "" ? null : Number(form.due_cycles),
      });
      toast.success("Job created");
      setForm({ title: "", priority: "Medium", due_date: "", assignee_id: "", affected_area: "", due_hours: "", due_cycles: "" });
      setShowNew(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create job");
    } finally {
      setBusy(false);
    }
  };

  const saveAssign = async () => {
    setBusy(true);
    try {
      await api.patch(`/maintenance/work-orders/${assigning.id}/assign`, { assignee_ids: assignList.map((a) => a.id) });
      toast.success(assignList.length ? "Assignees updated — job added to their Tasks" : "Job unassigned");
      setAssigning(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    } finally {
      setBusy(false);
    }
  };

  const complete = async (wo) => {
    if (doneForm.labor_hours === "" || Number(doneForm.labor_hours) <= 0) { toast.error("Allocated hours are required"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/work-orders/${wo.id}/complete`, { ...doneForm, labor_hours: Number(doneForm.labor_hours) });
      toast.success("Job completed — logged");
      setCompleting(null);
      setDoneForm({ notes: "", parts: "", labor_hours: "" });
      if (wo.us_flag && ac.status === "unserviceable") setUsPrompt(wo);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete");
    } finally {
      setBusy(false);
    }
  };

  const returnToService = async () => {
    const melCount = (ac.mel_items || []).length;
    try {
      if (melCount > 0) {
        await api.post(`/maintenance/aircraft/${ac.tail}/status`, { status: "mel" });
        toast.warning(`${ac.tail} set to MEL — ${melCount} open MEL item${melCount === 1 ? "" : "s"} must be cleared before it can be made Serviceable`);
      } else {
        await api.post(`/maintenance/aircraft/${ac.tail}/status`, { status: "normal" });
        toast.success(`${ac.tail} returned to service — Serviceable`);
      }
      setUsPrompt(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const remove = async (wo) => {
    if (!window.confirm(`Delete job "${wo.title}"?`)) return;
    try {
      await api.delete(`/maintenance/work-orders/${wo.id}`);
      toast.success("Job deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const onTolerance = async (wo, on) => {
    if (on) { setTolWO(wo); return; }
    if (!window.confirm("Remove tolerance? The due fields revert to their original values.")) return;
    try {
      await api.delete(`/maintenance/work-orders/${wo.id}/tolerance`);
      toast.success("Tolerance removed — dues reverted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove tolerance");
    }
  };

  return (
    <div>
      <ColumnHead>
        <span className="flex items-center justify-between">
          <span>Open Jobs / Tasks ({open.length})</span>
          <button className="inline-flex items-center gap-1 text-summit-primary hover:underline normal-case tracking-normal" onClick={() => setShowNew(true)} data-testid="maint-wo-new-toggle">
            <Plus className="w-3 h-3" /> Add
          </button>
        </span>
      </ColumnHead>
      <div className="space-y-2" data-testid="maint-wo-list">
        {open.length === 0 && <p className="text-xs text-gray-400">No open jobs or tasks.</p>}
        {open.map((wo) => {
          const flag = dueFlag(wo, ac);
          return (
            <div key={wo.id} className={`border rounded-md px-2.5 py-2 bg-white ${wo.us_flag ? "border-red-400" : "border-summit-hairline"}`} data-testid={`maint-wo-${wo.id}`}>
              <div className="flex items-start gap-2 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 ${PRIORITY_CLS[wo.priority]}`}>{wo.priority}</span>
                {wo.us_flag && (
                  <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 bg-red-600 text-white" title="This jobcard is the reason the aircraft is Unserviceable" data-testid={`maint-wo-usflag-${wo.id}`}>
                    U/S reason
                  </span>
                )}
                {flag && (
                  <span className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 ${flag.cls}`} title={flag.detail} data-testid={`maint-wo-flag-${wo.id}`}>
                    {flag.label} · {flag.detail}
                  </span>
                )}
                {wo.tol_applied && (
                  <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 bg-amber-100 text-amber-800 border border-amber-300" title={`Tolerance applied by ${wo.tol_by_name || "unknown"}${wo.tol_at ? ` · ${fmtWhen(wo.tol_at)}` : ""}`} data-testid={`maint-wo-tol-chip-${wo.id}`}>
                    On tolerance {[wo.tol_hours ? `+${wo.tol_hours}h` : null, wo.tol_cycles ? `+${wo.tol_cycles}c` : null, wo.tol_days ? `+${wo.tol_days}d` : null].filter(Boolean).join(" · ")}
                  </span>
                )}
                <span className="text-xs font-medium leading-snug whitespace-pre-wrap w-full">{wo.title}</span>
                {(user.role === "admin" || wo.created_by === user.id) && (
                  <button className="ml-auto p-0.5 rounded text-gray-300 hover:text-rose-600 shrink-0 -mt-6" onClick={() => remove(wo)} data-testid={`maint-wo-delete-${wo.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {wo.affected_area && <div className="text-[10px] text-gray-600 mt-1" data-testid={`maint-wo-area-${wo.id}`}>Area: {wo.affected_area}</div>}
              {(wo.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`maint-wo-attachments-${wo.id}`}>
                  {wo.attachments.map((a) => (
                    <a key={a.id} href={`${API_BASE}/planning/files/${a.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-summit-primary hover:underline border border-summit-hairline rounded-full px-2 py-0.5 bg-white max-w-[11rem]" data-testid={`maint-wo-attach-${a.id}`}>
                      <Paperclip className="w-3 h-3 shrink-0" /> <span className="truncate">{a.filename}</span>
                    </a>
                  ))}
                </div>
              )}
              <div className="text-[10px] text-gray-500 mt-1">
                {(() => { const names = wo.assignees?.length ? wo.assignees.map((a) => a.name).join(", ") : wo.assignee_name; return names ? `→ ${names}` : "Unassigned"; })()} · by {wo.created_by_name}
                {wo.due_date && (
                  <span className={`ml-1 font-mono ${isOverdue(wo.due_date) ? "text-rose-600 font-bold" : ""}`}>
                    · due {wo.due_date}
                    {(() => {
                      const d = Math.round((new Date(wo.due_date + "T00:00:00") - new Date(todayIso() + "T00:00:00")) / 86400000);
                      return (
                        <span className={d <= 0 ? "text-rose-600 font-bold" : d <= 14 ? "text-amber-600 font-bold" : "text-gray-400"} data-testid={`maint-wo-days-left-${wo.id}`}>
                          {" "}({d <= 0 ? `${Math.abs(d)} days over` : `${d} days left`})
                        </span>
                      );
                    })()}
                  </span>
                )}
                {wo.due_hours != null && <span className="ml-1 font-mono">· @{wo.due_hours} hrs</span>}
                {wo.due_cycles != null && <span className="ml-1 font-mono">· @{wo.due_cycles} cyc</span>}
              </div>
              <div className="mt-1.5 flex items-center gap-3">
                <button className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline" onClick={() => { setCompleting(wo); setDoneForm({ notes: "", parts: "", labor_hours: "" }); }} data-testid={`maint-wo-complete-${wo.id}`}>
                  <CheckCircle2 className="w-3 h-3" /> Complete
                </button>
                <button className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline" onClick={() => { setAssigning(wo); setAssignId(""); setAssignList(wo.assignees?.length ? wo.assignees : (wo.assignee_id ? [{ id: wo.assignee_id, name: wo.assignee_name }] : [])); }} data-testid={`maint-wo-assign-${wo.id}`}>
                  <UserPlus className="w-3 h-3" /> Assign
                </button>
                <span className="inline-flex items-center gap-1.5" title={wo.tol_applied ? `Tolerance applied by ${wo.tol_by_name || "unknown"}${wo.tol_at ? ` · ${fmtWhen(wo.tol_at)}` : ""}` : "Apply tolerance to this job's dues"}>
                  <Switch className="scale-[0.65] -mx-1" checked={!!wo.tol_applied} onCheckedChange={(v) => onTolerance(wo, v)} data-testid={`maint-wo-tol-toggle-${wo.id}`} />
                  <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500">Tolerance</span>
                </span>
                <button
                  className={`inline-flex items-center gap-1 text-[11px] ${openComments[wo.id] ? "text-summit-primary" : "text-gray-400 hover:text-summit-primary"}`}
                  onClick={() => setOpenComments((m) => ({ ...m, [wo.id]: !m[wo.id] }))}
                  title={`${(wo.comments || []).length} comment${(wo.comments || []).length === 1 ? "" : "s"}`}
                  data-testid={`maint-wo-comments-toggle-${wo.id}`}
                >
                  <MessageSquare className="w-3 h-3" />
                  {(wo.comments || []).length > 0 && <span className="font-mono text-[10px]">{(wo.comments || []).length}</span>}
                </button>
              </div>
              {openComments[wo.id] && (
                <div className="mt-2 border-t border-summit-hairline pt-2 space-y-1.5" data-testid={`maint-wo-comments-${wo.id}`}>
                  {(wo.comments || []).length === 0 && <p className="text-[10px] text-gray-400">No comments yet.</p>}
                  {(wo.comments || []).map((c) => (
                    <div key={c.id} className="text-[11px] bg-summit-canvas/50 rounded px-2 py-1" data-testid={`maint-wo-comment-${c.id}`}>
                      <span className="whitespace-pre-wrap">{c.text}</span>
                      <div className="text-[9px] text-gray-400 mt-0.5">{c.by_name} · {fmtWhen(c.created_at)}</div>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <Input
                      className="h-7 text-xs"
                      placeholder="Add a comment…"
                      value={commentText[wo.id] || ""}
                      onChange={(e) => setCommentText((m) => ({ ...m, [wo.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") addComment(wo); }}
                      data-testid={`maint-wo-comment-input-${wo.id}`}
                    />
                    <button className="p-1.5 rounded text-summit-primary hover:bg-summit-glacier/60 shrink-0" onClick={() => addComment(wo)} title="Send comment" data-testid={`maint-wo-comment-add-${wo.id}`}>
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md" data-testid="maint-wo-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ClipboardList className="w-4 h-4" /> New job / task</DialogTitle>
            <DialogDescription>{ac.tail}{ac.fleet ? ` — ${ac.fleet}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Work needed *</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="e.g. replace main gear tire" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="maint-wo-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Affected area</Label>
              <Input className="mt-1" placeholder="e.g. landing gear, hydraulic system" value={form.affected_area} onChange={(e) => setForm((f) => ({ ...f, affected_area: e.target.value }))} data-testid="maint-wo-area" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Priority</Label>
                <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} data-testid="maint-wo-priority">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Due date</Label>
                <Input className="mt-1" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="maint-wo-due" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Due at hours</Label>
                <Input className="mt-1" type="number" min="0" step="0.1" placeholder={ac.airframe_hours != null ? `now ${ac.airframe_hours}` : "optional"} value={form.due_hours} onChange={(e) => setForm((f) => ({ ...f, due_hours: e.target.value }))} data-testid="maint-wo-due-hours" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Due at cycles</Label>
                <Input className="mt-1" type="number" min="0" step="1" placeholder={ac.cycles != null ? `now ${ac.cycles}` : "optional"} value={form.due_cycles} onChange={(e) => setForm((f) => ({ ...f, due_cycles: e.target.value }))} data-testid="maint-wo-due-cycles" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Assign to</Label>
              <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={form.assignee_id} onChange={(e) => setForm((f) => ({ ...f, assignee_id: e.target.value }))} data-testid="maint-wo-assignee">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={create} disabled={busy} data-testid="maint-wo-submit">Create job / task</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completing} onOpenChange={(o) => { if (!o) setCompleting(null); }}>
        <DialogContent className="max-w-md" data-testid={`maint-wo-complete-form-${completing?.id || ""}`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Complete job / task</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{completing?.title}{completing?.affected_area ? ` — ${completing.affected_area}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Hours allocated *</Label>
              <Input className="mt-1" type="number" min="0.1" step="0.1" placeholder="e.g. 2.5" value={doneForm.labor_hours} onChange={(e) => setDoneForm((f) => ({ ...f, labor_hours: e.target.value }))} data-testid="maint-wo-done-hours" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Work performed / notes</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="What was done?" value={doneForm.notes} onChange={(e) => setDoneForm((f) => ({ ...f, notes: e.target.value }))} data-testid="maint-wo-done-notes" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Parts used (optional)</Label>
              <Input className="mt-1" value={doneForm.parts} onChange={(e) => setDoneForm((f) => ({ ...f, parts: e.target.value }))} data-testid="maint-wo-done-parts" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => complete(completing)} data-testid="maint-wo-done-confirm">Mark complete</Button>
          </div>
        </DialogContent>
      </Dialog>

      <WOToleranceDialog wo={tolWO} onClose={() => setTolWO(null)} onApplied={onSaved} />

      <Dialog open={!!assigning} onOpenChange={(o) => { if (!o) setAssigning(null); }}>
        <DialogContent className="max-w-md" data-testid="maint-wo-assign-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><UserPlus className="w-4 h-4" /> Assign job</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{assigning?.title?.slice(0, 120)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {assignList.length > 0 && (
              <div className="space-y-1.5" data-testid="maint-wo-assign-list">
                {assignList.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 border border-summit-hairline rounded-md px-2.5 py-1.5 bg-summit-canvas/50" data-testid={`maint-wo-assignee-${a.id}`}>
                    <span className="text-sm">{a.name}</span>
                    <button className="ml-auto p-0.5 rounded text-gray-400 hover:text-rose-600" onClick={() => setAssignList((l) => l.filter((x) => x.id !== a.id))} data-testid={`maint-wo-assignee-remove-${a.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <EmployeeSearchSelect
                  options={members.filter((m) => !assignList.some((a) => a.id === m.id)).map((m) => ({ id: m.id, label: m.name, sublabel: m.job_title }))}
                  value={assignId}
                  onChange={setAssignId}
                  triggerPlaceholder="Search & pick a person"
                  testId="maint-wo-assign-select"
                />
              </div>
              <Button
                variant="outline"
                className="h-10 px-3"
                disabled={!assignId}
                onClick={() => {
                  const m = members.find((x) => x.id === assignId);
                  if (m) setAssignList((l) => [...l, { id: m.id, name: m.name }]);
                  setAssignId("");
                }}
                data-testid="maint-wo-assign-add"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-gray-500">Each assignee gets the job on their Tasks page. Closing it from either side keeps everything in sync — when someone completes the job it leaves the others&apos; Tasks automatically.</p>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={busy} onClick={saveAssign} data-testid="maint-wo-assign-save">
              {assignList.length ? `Save ${assignList.length} assignee${assignList.length > 1 ? "s" : ""}` : "Save (unassign all)"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!usPrompt} onOpenChange={(o) => { if (!o) setUsPrompt(null); }}>
        <AlertDialogContent data-testid="maint-us-rts-prompt">
          <AlertDialogHeader>
            <AlertDialogTitle>Return {ac.tail} to service?</AlertDialogTitle>
            <AlertDialogDescription>
              {(ac.mel_items || []).length > 0
                ? <>The jobcard that grounded this aircraft has been completed, but {ac.tail} still has <b>{(ac.mel_items || []).length} open MEL item{(ac.mel_items || []).length === 1 ? "" : "s"}</b> — it will return to <b>MEL</b> status. MEL items must be cleared before it can be made Serviceable.</>
                : <>The jobcard that grounded this aircraft has been completed. Change {ac.tail} back to <b>Serviceable</b>?</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="maint-us-rts-dismiss">Keep Unserviceable</AlertDialogCancel>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={returnToService} data-testid="maint-us-rts-confirm">
              {(ac.mel_items || []).length > 0 ? "Return to MEL" : "Return to Serviceable"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function WorkLog({ ac, onSaved }) {
  const [showAdd, setShowAdd] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchPage, setSearchPage] = useState(0);
  const [searchDetail, setSearchDetail] = useState(null);
  const [form, setForm] = useState({ date: todayIso(), work: "", parts: "", labor_hours: "" });
  const [busy, setBusy] = useState(false);
  const log = [...(ac.work_log || [])].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.created_at || "").localeCompare(String(a.created_at || "")));
  const active = log.filter((l) => !l.archived);
  const archived = log.filter((l) => l.archived);
  const totalHours = active.reduce((n, l) => n + (Number(l.labor_hours) || 0), 0);

  const add = async () => {
    if (!form.work.trim()) { toast.error("Describe the work done"); return; }
    if (form.labor_hours === "" || Number(form.labor_hours) <= 0) { toast.error("Allocated hours are required"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/aircraft/${ac.tail}/log`, { ...form, labor_hours: Number(form.labor_hours) });
      toast.success("Logged");
      setForm({ date: todayIso(), work: "", parts: "", labor_hours: "" });
      setShowAdd(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log work");
    } finally {
      setBusy(false);
    }
  };

  const setArchived = async (l, val) => {
    try {
      await api.patch(`/maintenance/aircraft/${ac.tail}/log/${l.id}`, { archived: val });
      toast.success(val ? "Entry archived" : "Entry restored");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update entry");
    }
  };

  const renderEntry = (l) => (
    <div key={l.id} className={`border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white text-xs ${l.archived ? "opacity-60" : ""}`} data-testid={`maint-log-${l.id}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-gray-400">{l.date}</span>
        <span className="font-medium text-[11px]">{l.by_name}</span>
        {l.labor_hours ? <span className="font-mono text-[10px] text-sky-700">{l.labor_hours} hrs</span> : null}
        {l.work_order_id && <span className="font-mono text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 px-1.5 rounded">WO</span>}
        {l.archived && <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400 bg-gray-100 px-1.5 rounded">Archived</span>}
        <button
          className="ml-auto p-0.5 rounded text-gray-300 hover:text-gray-600 shrink-0 self-center"
          onClick={() => setArchived(l, !l.archived)}
          title={l.archived ? "Restore entry" : "Archive entry"}
          data-testid={`maint-log-${l.archived ? "unarchive" : "archive"}-${l.id}`}
        >
          {l.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="mt-0.5 whitespace-pre-wrap">{l.work}</div>
      {l.affected_area && <div className="text-[10px] text-gray-500 mt-0.5">Area: {l.affected_area}</div>}
      {l.parts && <div className="text-[10px] text-gray-500 mt-0.5">Parts: {l.parts}</div>}
    </div>
  );

  const q = searchQ.trim().toLowerCase();
  const results = q ? log.filter((l) => [l.work, l.parts, l.by_name, l.date, l.affected_area].some((v) => (v || "").toLowerCase().includes(q))) : [];

  return (
    <div>
      <ColumnHead>
        <span className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1"><History className="w-3 h-3" /> Completed Jobs / Tasks ({active.length}{totalHours > 0 ? ` · ${Math.round(totalHours * 10) / 10} hrs` : ""})</span>
          <span className="inline-flex items-center gap-2.5">
            <button className="text-gray-400 hover:text-summit-primary" onClick={() => { setSearchQ(""); setSearchPage(0); setSearchDetail(null); setShowSearch(true); }} title="Search work history" data-testid="maint-log-search-toggle">
              <Search className="w-3.5 h-3.5" />
            </button>
            <button className="inline-flex items-center gap-1 text-summit-primary hover:underline normal-case tracking-normal" onClick={() => setShowAdd(true)} data-testid="maint-log-add-toggle">
              <Plus className="w-3 h-3" /> Add
            </button>
          </span>
        </span>
      </ColumnHead>
      <div className="space-y-1.5 max-h-[50rem] overflow-y-auto pr-1" data-testid="maint-log-list">
        {active.length === 0 && archived.length === 0 && <p className="text-xs text-gray-400">No work logged yet.</p>}
        {active.map(renderEntry)}
        {showArchived && archived.map(renderEntry)}
      </div>
      {archived.length > 0 && (
        <button
          className="mt-1.5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gray-400 hover:text-gray-600"
          onClick={() => setShowArchived((v) => !v)}
          data-testid="maint-log-show-archived"
        >
          <Archive className="w-3 h-3" /> {showArchived ? "Hide" : "Show"} archived ({archived.length})
        </button>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="maint-log-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><History className="w-4 h-4" /> Add completed job / task</DialogTitle>
            <DialogDescription>{ac.tail}{ac.fleet ? ` — ${ac.fleet}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Date</Label>
                <Input className="mt-1" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="maint-log-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Hours allocated *</Label>
                <Input className="mt-1" type="number" min="0.1" step="0.1" placeholder="e.g. 1.5" value={form.labor_hours} onChange={(e) => setForm((f) => ({ ...f, labor_hours: e.target.value }))} data-testid="maint-log-hours" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Work performed *</Label>
              <Textarea className="mt-1 text-sm" rows={4} placeholder="What was done?" value={form.work} onChange={(e) => setForm((f) => ({ ...f, work: e.target.value }))} data-testid="maint-log-work" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Parts used (optional)</Label>
              <Input className="mt-1" value={form.parts} onChange={(e) => setForm((f) => ({ ...f, parts: e.target.value }))} data-testid="maint-log-parts" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="maint-log-submit">Add entry</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSearch} onOpenChange={(o) => { setShowSearch(o); if (!o) { setSearchDetail(null); setSearchPage(0); } }}>
        <DialogContent className="max-w-lg" data-testid="maint-log-search-dialog">
          {searchDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2"><History className="w-4 h-4" /> Work details</DialogTitle>
                <DialogDescription>{ac.tail} — {searchDetail.date}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3" data-testid="maint-log-search-detail">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Date</span>{searchDetail.date}</div>
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Logged by</span>{searchDetail.by_name}</div>
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Hours allocated</span>{searchDetail.labor_hours || "—"}</div>
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Source</span>{searchDetail.work_order_id ? "Job / task" : searchDetail.problem_id ? "Problem report" : "Manual entry"}</div>
                  {searchDetail.affected_area && (
                    <div className="col-span-2"><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Affected area</span>{searchDetail.affected_area}</div>
                  )}
                  {searchDetail.parts && (
                    <div className="col-span-2"><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Parts used</span>{searchDetail.parts}</div>
                  )}
                </div>
                <div>
                  <span className="text-gray-400 text-[10px] uppercase tracking-widest block mb-1">Work performed</span>
                  <div className="border border-summit-hairline rounded-md bg-summit-canvas/40 p-3 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">{searchDetail.work}</div>
                </div>
                <Button variant="outline" className="w-full" onClick={() => setSearchDetail(null)} data-testid="maint-log-search-back">
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to results
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2"><Search className="w-4 h-4" /> Search work history</DialogTitle>
                <DialogDescription>{ac.tail} — searches all {log.length} entries, including archived</DialogDescription>
              </DialogHeader>
              <Input placeholder="Search work, parts, area, person or date…" value={searchQ} onChange={(e) => { setSearchQ(e.target.value); setSearchPage(0); }} autoFocus data-testid="maint-log-search-input" />
              <div className="space-y-1.5 max-h-[30rem] overflow-y-auto pr-1" data-testid="maint-log-search-results">
                {q === "" ? (
                  <p className="text-xs text-gray-400">Type to search previous work and details.</p>
                ) : results.length === 0 ? (
                  <p className="text-xs text-gray-400">No matching entries.</p>
                ) : (
                  <>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                      {results.length} match{results.length === 1 ? "" : "es"} — click an entry for full details
                    </p>
                    {results.slice(searchPage * 10, searchPage * 10 + 10).map((l) => (
                      <button
                        key={l.id}
                        className="w-full text-left border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white text-xs hover:bg-summit-glacier/40 transition-colors"
                        onClick={() => setSearchDetail(l)}
                        data-testid={`maint-log-search-result-${l.id}`}
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono text-[10px] text-gray-400">{l.date}</span>
                          <span className="font-medium text-[11px]">{l.by_name}</span>
                          {l.labor_hours ? <span className="font-mono text-[10px] text-sky-700">{l.labor_hours} hrs</span> : null}
                          {l.work_order_id && <span className="font-mono text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 px-1.5 rounded">WO</span>}
                          {l.archived && <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400 bg-gray-100 px-1.5 rounded">Archived</span>}
                        </div>
                        <div className="mt-0.5 truncate text-gray-700">{l.work}</div>
                        {l.affected_area && <div className="text-[10px] text-gray-500 mt-0.5">Area: {l.affected_area}</div>}
                      </button>
                    ))}
                  </>
                )}
              </div>
              {results.length > 10 && (
                <div className="flex items-center justify-between border-t border-summit-hairline pt-2">
                  <button className="font-mono text-[11px] text-summit-primary hover:underline disabled:opacity-30 disabled:no-underline" disabled={searchPage === 0} onClick={() => setSearchPage((p) => p - 1)} data-testid="maint-log-search-prev">‹ Prev</button>
                  <span className="font-mono text-[10px] text-gray-400">Page {searchPage + 1} of {Math.ceil(results.length / 10)}</span>
                  <button className="font-mono text-[11px] text-summit-primary hover:underline disabled:opacity-30 disabled:no-underline" disabled={searchPage >= Math.ceil(results.length / 10) - 1} onClick={() => setSearchPage((p) => p + 1)} data-testid="maint-log-search-next">Next ›</button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMPTY_MEL_FORM = { description: "", category: "B", next_calendar_due: "", date_applied: "", mel_ref: "", ops_restriction: false, parts_on_order: false, comment: "" };

function MelCard({ ac, onSaved }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_MEL_FORM });
  const [busy, setBusy] = useState(false);
  const items = ac.mel_items || [];

  const add = async () => {
    if (!form.description.trim()) { toast.error("MEL description is required"); return; }
    if (!form.next_calendar_due) { toast.error("Expiry date is required"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/aircraft/${ac.tail}/mel`, form);
      toast.success("MEL item added");
      setForm({ ...EMPTY_MEL_FORM });
      setShowAdd(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add MEL item");
    } finally {
      setBusy(false);
    }
  };

  const clear = async (m) => {
    if (!window.confirm(`Clear MEL item "${m.description}"?`)) return;
    try {
      await api.delete(`/maintenance/aircraft/${ac.tail}/mel/${m.id}`);
      toast.success("MEL item cleared");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear MEL item");
    }
  };

  return (
    <div className="mb-5" data-testid={`maint-mel-card-${ac.tail}`}>
      <ColumnHead>
        <span className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1"><FileWarning className="w-3 h-3" /> MEL items ({items.length})</span>
          <button className="inline-flex items-center gap-1 text-summit-primary hover:underline normal-case tracking-normal" onClick={() => setShowAdd(true)} data-testid="maint-mel-add">
            <Plus className="w-3 h-3" /> Add
          </button>
        </span>
      </ColumnHead>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-xs text-gray-400">No MEL items.</p>}
        {items.map((m) => (
          <div key={m.id} className="border border-amber-300 rounded-md px-2.5 py-2 bg-amber-50/60" data-testid={`maint-mel-item-${m.id}`}>
            <div className="flex items-start gap-2">
              <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 bg-amber-400 text-amber-950">CAT {m.category}</span>
              <span className="text-xs leading-snug whitespace-pre-wrap">{m.description}</span>
              <button className="ml-auto p-0.5 rounded text-gray-300 hover:text-rose-600 shrink-0" onClick={() => clear(m)} title="Clear MEL item" data-testid={`maint-mel-clear-${m.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {(m.mel_ref || m.ops_restriction || m.parts_on_order) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                {m.mel_ref && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300">{m.mel_ref}</span>}
                {m.ops_restriction && <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 font-bold">Ops restriction</span>}
                {m.parts_on_order && <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-300">Parts on order</span>}
              </div>
            )}
            {m.comment && <div className="text-[10px] text-gray-600 italic mt-1 whitespace-pre-wrap">“{m.comment}”</div>}
            <div className="text-[10px] text-gray-500 mt-1">
              {m.date_applied && <span className="font-mono">applied {m.date_applied} · </span>}
              <span className={`font-mono ${isOverdue(m.next_calendar_due) ? "text-rose-600 font-bold" : ""}`}>
                expires {m.next_calendar_due}{isOverdue(m.next_calendar_due) ? " · OVERDUE" : ""}
              </span>
              {" · "}{m.added_by_name} · {fmtWhen(m.created_at)}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="maint-mel-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><FileWarning className="w-4 h-4 text-amber-500" /> Add MEL item</DialogTitle>
            <DialogDescription>{ac.tail}{ac.fleet ? ` — ${ac.fleet}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Description *</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="Deferred defect description…" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="maint-mel-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">MEL category *</Label>
                <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="maint-mel-category">
                  {MEL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">MEL# / Case#</Label>
                <Input className="mt-1" placeholder="e.g. 33-10-1 Case A" value={form.mel_ref} onChange={(e) => setForm((f) => ({ ...f, mel_ref: e.target.value }))} data-testid="maint-mel-ref" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Date applied</Label>
                <Input className="mt-1" type="date" value={form.date_applied} onChange={(e) => setForm((f) => ({ ...f, date_applied: e.target.value }))} data-testid="maint-mel-date-applied" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Expiry date *</Label>
                <Input className="mt-1" type="date" value={form.next_calendar_due} onChange={(e) => setForm((f) => ({ ...f, next_calendar_due: e.target.value }))} data-testid="maint-mel-due" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Ops restriction?</Label>
                <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={form.ops_restriction ? "yes" : "no"} onChange={(e) => setForm((f) => ({ ...f, ops_restriction: e.target.value === "yes" }))} data-testid="maint-mel-ops-restriction">
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Parts on order?</Label>
                <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={form.parts_on_order ? "yes" : "no"} onChange={(e) => setForm((f) => ({ ...f, parts_on_order: e.target.value === "yes" }))} data-testid="maint-mel-parts-on-order">
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Comment</Label>
              <Textarea className="mt-1 text-sm" rows={2} placeholder="e.g. troubleshooting in progress…" value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} data-testid="maint-mel-comment" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="maint-mel-submit">Add MEL item</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProblemReports({ ac, user, onSaved }) {
  const [showAdd, setShowAdd] = useState(false);
  const [text, setText] = useState("");
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState("");
  const [resolveHours, setResolveHours] = useState("");
  const [busy, setBusy] = useState(false);
  const open = (ac.problem_reports || []).filter((p) => p.status === "open");

  const add = async () => {
    if (!text.trim()) { toast.error("Describe the problem"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/aircraft/${ac.tail}/problems`, { text });
      toast.success("Problem reported");
      setText("");
      setShowAdd(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to report problem");
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    if (resolveHours === "" || Number(resolveHours) <= 0) { toast.error("Allocated hours are required"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/aircraft/${ac.tail}/problems/${resolving.id}/resolve`, { resolution, labor_hours: Number(resolveHours) });
      toast.success("Problem resolved — logged");
      setResolving(null);
      setResolution("");
      setResolveHours("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this problem report?")) return;
    try {
      await api.delete(`/maintenance/aircraft/${ac.tail}/problems/${p.id}`);
      toast.success("Problem report deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div>
      <ColumnHead>
        <span className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Problem reports ({open.length})</span>
          <button className="inline-flex items-center gap-1 text-summit-primary hover:underline normal-case tracking-normal" onClick={() => setShowAdd(true)} data-testid="maint-pr-add-toggle">
            <Plus className="w-3 h-3" /> Report
          </button>
        </span>
      </ColumnHead>
      <div className="space-y-2" data-testid="maint-pr-list">
        {open.length === 0 && <p className="text-xs text-gray-400">No open problems.</p>}
        {open.map((p) => (
          <div key={p.id} className="border border-rose-200 rounded-md px-2.5 py-2 bg-rose-50/50" data-testid={`maint-pr-${p.id}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
              <span className="text-xs leading-snug whitespace-pre-wrap">{p.text}</span>
              {(user.role === "admin" || p.reported_by === user.id) && (
                <button className="ml-auto p-0.5 rounded text-gray-300 hover:text-rose-600 shrink-0" onClick={() => remove(p)} data-testid={`maint-pr-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">{p.reported_by_name} · {fmtWhen(p.created_at)}</div>
            <div className="mt-1.5">
              <button className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline" onClick={() => { setResolving(p); setResolution(""); setResolveHours(""); }} data-testid={`maint-pr-resolve-${p.id}`}>
                <CheckCircle2 className="w-3 h-3" /> Resolve
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="maint-pr-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-500" /> Report a problem</DialogTitle>
            <DialogDescription>{ac.tail}{ac.fleet ? ` — ${ac.fleet}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">What&apos;s wrong? *</Label>
              <Textarea className="mt-1 text-sm" rows={4} placeholder="Describe the problem in detail…" value={text} onChange={(e) => setText(e.target.value)} data-testid="maint-pr-text" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="maint-pr-submit">Report problem</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolving} onOpenChange={(o) => { if (!o) setResolving(null); }}>
        <DialogContent className="max-w-md" data-testid={`maint-pr-resolve-form-${resolving?.id || ""}`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Resolve problem</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{resolving?.text}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Hours allocated *</Label>
              <Input className="mt-1" type="number" min="0.1" step="0.1" placeholder="e.g. 1.5" value={resolveHours} onChange={(e) => setResolveHours(e.target.value)} data-testid="maint-pr-resolve-hours" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Details of work done (optional)</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="Resolution details…" value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="maint-pr-resolution" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={resolve} data-testid="maint-pr-resolve-confirm">Mark resolved</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const QE_FIELDS = {
  log_number: { label: "Log #", type: "text", placeholder: "e.g. 4521" },
  airframe_hours: { label: "Aircraft hours", type: "number", step: "0.1", placeholder: "e.g. 12345.6" },
  cycles: { label: "Cycles", type: "number", step: "1", placeholder: "e.g. 8210" },
  next_calendar_due: { label: "Next calendar due", type: "date" },
  base: { label: "Base", type: "select" },
};

function QuickEditDialog({ ac, field, onClose, onSaved }) {
  const cfg = QE_FIELDS[field] || {};
  const [value, setValue] = useState("");
  const [cities, setCities] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (ac && field) setValue(ac[field] ?? "");
    if (field === "base") api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, [ac, field]);

  const save = async () => {
    if (cfg.type === "number" && value !== "" && Number(value) < 0) { toast.error(`${cfg.label} can't be negative`); return; }
    setBusy(true);
    try {
      const v = value === "" ? null : (cfg.type === "number" ? Number(value) : value);
      await api.patch(`/maintenance/aircraft/${ac.tail}/details`, { [field]: v });
      toast.success(`${ac.tail} — ${cfg.label} updated`);
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  if (!ac || !field) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xs" data-testid="maint-quick-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Pencil className="w-4 h-4" /> {ac.tail} — {cfg.label}</DialogTitle>
          <DialogDescription>Used for due auto-calculations on jobs.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {cfg.type === "select" ? (
            <select
              className="h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm"
              value={value || ""}
              onChange={(e) => setValue(e.target.value)}
              disabled={cities.length === 0}
              data-testid="maint-quick-input"
            >
              <option value="">{cities.length === 0 ? "Loading bases…" : "No base"}</option>
              {cities.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          ) : (
            <Input
              type={cfg.type}
              step={cfg.step}
              min={cfg.type === "number" ? "0" : undefined}
              placeholder={cfg.placeholder}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              data-testid="maint-quick-input"
            />
          )}
          <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={busy} data-testid="maint-quick-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ ac, open, onOpenChange, onSaved }) {
  const [status, setStatus] = useState("normal");
  const [reason, setReason] = useState("");
  const [rtsDate, setRtsDate] = useState("");
  const [rtsTime, setRtsTime] = useState("");
  const [mel, setMel] = useState({ description: "", category: "B", next_due: "", date_applied: "", mel_ref: "", ops_restriction: false, parts_on_order: false, comment: "" });
  const [aogParts, setAogParts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && ac) {
      setStatus(ac.status || "normal");
      setReason("");
      setRtsDate("");
      setRtsTime("");
      setMel({ description: "", category: "B", next_due: "", date_applied: "", mel_ref: "", ops_restriction: false, parts_on_order: false, comment: "" });
      setAogParts((ac.aog_parts || []).map((p) => ({ part: p.part || "", ship_to: p.ship_to || "", eta: p.eta || "", pr_po: p.pr_po || "", comment: p.comment || "" })));
    }
  }, [open, ac]);

  const setAog = (i, k, v) => setAogParts((l) => l.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    const payload = { status };
    if (status === "unserviceable") {
      if (!reason.trim()) { toast.error("A reason is required for Unserviceable"); return; }
      if (!rtsDate || !rtsTime) { toast.error("Anticipated return-to-service date & time is required"); return; }
      payload.reason = reason.trim();
      payload.return_to_service = `${rtsDate}T${rtsTime}`;
      payload.aog_parts = aogParts.filter((p) => p.part.trim());
    }
    if (status === "mel") {
      const hasExisting = (ac.mel_items || []).length > 0;
      if (mel.description.trim() || mel.next_due || !hasExisting) {
        if (!mel.description.trim()) { toast.error("MEL description is required"); return; }
        if (!mel.next_due) { toast.error("Expiry date is required"); return; }
        payload.mel_description = mel.description.trim();
        payload.mel_category = mel.category;
        payload.mel_next_due = mel.next_due;
        payload.mel_date_applied = mel.date_applied || null;
        payload.mel_ref = mel.mel_ref.trim() || null;
        payload.mel_ops_restriction = mel.ops_restriction;
        payload.mel_parts_on_order = mel.parts_on_order;
        payload.mel_comment = mel.comment.trim() || null;
      }
    }
    setBusy(true);
    try {
      await api.post(`/maintenance/aircraft/${ac.tail}/status`, payload);
      toast.success(`${ac.tail} → ${(AC_STATUS[status] || {}).label || status}`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  if (!ac) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="maint-status-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Pencil className="w-4 h-4" /> {ac.tail} — update status</DialogTitle>
          <DialogDescription>Current: {(AC_STATUS[ac.status] || AC_STATUS.normal).label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-1.5">
            {Object.entries(AC_STATUS).map(([code, meta]) => (
              <button
                key={code}
                type="button"
                className={`flex items-center gap-2.5 border rounded-md px-3 py-2 text-left text-sm transition-colors ${status === code ? "border-summit-primary bg-summit-glacier/50 font-medium" : "border-summit-hairline hover:bg-summit-glacier/30"}`}
                onClick={() => setStatus(code)}
                data-testid={`maint-status-opt-${code}`}
              >
                <Plane className={`w-4 h-4 shrink-0 ${meta.plane}`} fill="currentColor" />
                {meta.label}
                {status === code && <CheckCircle2 className="w-4 h-4 ml-auto text-summit-primary" />}
              </button>
            ))}
          </div>

          {status === "unserviceable" && (
            <div className="space-y-3 border border-red-200 rounded-md bg-red-50/50 p-3" data-testid="maint-us-fields">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Reason *</Label>
                <Textarea className="mt-1 text-sm bg-white" rows={2} placeholder="Why is the aircraft unserviceable?" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="maint-us-reason" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Return to service — date *</Label>
                  <Input className="mt-1 bg-white" type="date" value={rtsDate} onChange={(e) => setRtsDate(e.target.value)} data-testid="maint-us-rts-date" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Time *</Label>
                  <Input className="mt-1 bg-white" type="time" value={rtsTime} onChange={(e) => setRtsTime(e.target.value)} data-testid="maint-us-rts-time" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest text-gray-500">AOG parts on order</Label>
                  <button type="button" className="inline-flex items-center gap-1 text-[11px] text-summit-primary hover:underline" onClick={() => setAogParts((l) => [...l, { part: "", ship_to: "", eta: "", pr_po: "", comment: "" }])} data-testid="maint-us-aog-add">
                    <Plus className="w-3 h-3" /> Add part
                  </button>
                </div>
                {aogParts.length === 0 && <p className="text-[10px] text-gray-400 mt-1">No AOG parts on order.</p>}
                {aogParts.map((p, i) => (
                  <div key={i} className="mt-2 border border-red-200 rounded-md bg-white p-2 space-y-2" data-testid={`maint-us-aog-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input className="h-8 text-xs" placeholder="Part # / description *" value={p.part} onChange={(e) => setAog(i, "part", e.target.value)} data-testid={`maint-us-aog-part-${i}`} />
                      <button type="button" className="p-1 rounded text-gray-300 hover:text-rose-600 shrink-0" onClick={() => setAogParts((l) => l.filter((_, j) => j !== i))} title="Remove part" data-testid={`maint-us-aog-remove-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-gray-400">Ship to</Label>
                        <Input className="h-8 text-xs mt-0.5" placeholder="e.g. YZF" value={p.ship_to} onChange={(e) => setAog(i, "ship_to", e.target.value)} data-testid={`maint-us-aog-shipto-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-gray-400">ETA</Label>
                        <Input className="h-8 text-xs mt-0.5" type="date" value={p.eta} onChange={(e) => setAog(i, "eta", e.target.value)} data-testid={`maint-us-aog-eta-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-gray-400">PR/PO#</Label>
                        <Input className="h-8 text-xs mt-0.5" placeholder="e.g. PO-1234" value={p.pr_po} onChange={(e) => setAog(i, "pr_po", e.target.value)} data-testid={`maint-us-aog-prpo-${i}`} />
                      </div>
                    </div>
                    <Input className="h-8 text-xs" placeholder="Comment" value={p.comment} onChange={(e) => setAog(i, "comment", e.target.value)} data-testid={`maint-us-aog-comment-${i}`} />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-500">The reason is raised automatically as an open jobcard flagged “U/S reason”. Completing it will prompt a return to Serviceable. AOG parts appear on the fleet board.</p>
            </div>
          )}

          {status === "mel" && (
            <div className="space-y-3 border border-amber-300 rounded-md bg-amber-50/50 p-3" data-testid="maint-mel-fields">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Description *</Label>
                <Textarea className="mt-1 text-sm bg-white" rows={2} placeholder="Deferred defect description…" value={mel.description} onChange={(e) => setMel((m) => ({ ...m, description: e.target.value }))} data-testid="maint-status-mel-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">MEL category *</Label>
                  <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={mel.category} onChange={(e) => setMel((m) => ({ ...m, category: e.target.value }))} data-testid="maint-status-mel-category">
                    {MEL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">MEL# / Case#</Label>
                  <Input className="mt-1 bg-white" placeholder="e.g. 33-10-1 Case A" value={mel.mel_ref} onChange={(e) => setMel((m) => ({ ...m, mel_ref: e.target.value }))} data-testid="maint-status-mel-ref" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Date applied</Label>
                  <Input className="mt-1 bg-white" type="date" value={mel.date_applied} onChange={(e) => setMel((m) => ({ ...m, date_applied: e.target.value }))} data-testid="maint-status-mel-date-applied" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Expiry date *</Label>
                  <Input className="mt-1 bg-white" type="date" value={mel.next_due} onChange={(e) => setMel((m) => ({ ...m, next_due: e.target.value }))} data-testid="maint-status-mel-due" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Ops restriction?</Label>
                  <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={mel.ops_restriction ? "yes" : "no"} onChange={(e) => setMel((m) => ({ ...m, ops_restriction: e.target.value === "yes" }))} data-testid="maint-status-mel-ops">
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Parts on order?</Label>
                  <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={mel.parts_on_order ? "yes" : "no"} onChange={(e) => setMel((m) => ({ ...m, parts_on_order: e.target.value === "yes" }))} data-testid="maint-status-mel-parts">
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Comment</Label>
                <Textarea className="mt-1 text-sm bg-white" rows={2} placeholder="e.g. troubleshooting in progress…" value={mel.comment} onChange={(e) => setMel((m) => ({ ...m, comment: e.target.value }))} data-testid="maint-status-mel-comment" />
              </div>
              <p className="text-[10px] text-gray-500">Adds a MEL item to this aircraft — you can add more from the MEL card.</p>
            </div>
          )}

          <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={busy} data-testid="maint-status-save">Update status</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TroubleshootButton({ ac, user }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ata_chapter: "", jobcard: "", affected_part: "", details: "" });
  const [busy, setBusy] = useState(false);
  const [updateFor, setUpdateFor] = useState(null);
  const [updateText, setUpdateText] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    try {
      const r = await api.get(`/maintenance/aircraft/${encodeURIComponent(ac.tail)}/troubleshooting`);
      setList(r.data || []);
    } catch { setList([]); }
  };
  const openDialog = () => { setOpen(true); setSearch(""); setShowAdd(false); setUpdateFor(null); setPage(1); setList(null); load(); };

  const add = async () => {
    if (!form.ata_chapter.trim()) { toast.error("ATA chapter is required"); return; }
    if (!form.details.trim()) { toast.error("Troubleshooting details are required"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/aircraft/${encodeURIComponent(ac.tail)}/troubleshooting`, {
        ata_chapter: form.ata_chapter.trim(),
        jobcard: form.jobcard.trim(),
        affected_part: form.affected_part.trim(),
        details: form.details.trim(),
      });
      toast.success("Troubleshooting entry added");
      setForm({ ata_chapter: "", jobcard: "", affected_part: "", details: "" });
      setShowAdd(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add entry");
    } finally { setBusy(false); }
  };

  const addUpdate = async (tid) => {
    if (!updateText.trim()) { toast.error("Write the details first"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/troubleshooting/${tid}/updates`, { text: updateText.trim() });
      setUpdateFor(null);
      setUpdateText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add details");
    } finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm("Delete this troubleshooting entry?")) return;
    try {
      await api.delete(`/maintenance/troubleshooting/${t.id}`);
      toast.success("Entry deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete entry");
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = (list || []).filter((t) =>
    !q || [t.ata_chapter, t.jobcard, t.affected_part, t.details, ...(t.updates || []).map((u) => u.text)].some((v) => (v || "").toLowerCase().includes(q))
  );
  const PER_PAGE = 6;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((curPage - 1) * PER_PAGE, curPage * PER_PAGE);

  return (
    <>
      <button
        className="p-1.5 rounded hover:bg-white/15 transition-colors"
        onClick={openDialog}
        title={`Troubleshooting log — ${ac.tail}`}
        data-testid={`maint-ts-btn-${ac.tail}`}
      >
        <Stethoscope className="w-3.5 h-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="maint-ts-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Stethoscope className="w-4 h-4" /> Troubleshooting — {ac.tail}
            </DialogTitle>
            <DialogDescription>Search past troubleshooting or log a new entry so related snags are easy to trace.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="h-9 pl-8 text-sm" placeholder="Search ATA, jobcard, part, details…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="maint-ts-search" />
            </div>
            <Button size="sm" className="h-9 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => setShowAdd((s) => !s)} data-testid="maint-ts-add-toggle">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add troubleshooting
            </Button>
          </div>
          {showAdd && (
            <div className="border border-summit-hairline rounded-md p-3 space-y-3 bg-summit-canvas/40" data-testid="maint-ts-add-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">ATA chapter *</Label>
                  <Input className="mt-1 h-9 text-sm bg-white" placeholder="e.g. 32" value={form.ata_chapter} onChange={(e) => setForm((f) => ({ ...f, ata_chapter: e.target.value }))} data-testid="maint-ts-form-ata" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">Jobcard #</Label>
                  <Input className="mt-1 h-9 text-sm bg-white" placeholder="e.g. JC-1042" value={form.jobcard} onChange={(e) => setForm((f) => ({ ...f, jobcard: e.target.value }))} data-testid="maint-ts-form-jobcard" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">Affected part</Label>
                  <Input className="mt-1 h-9 text-sm bg-white" placeholder="e.g. MLG brake unit" value={form.affected_part} onChange={(e) => setForm((f) => ({ ...f, affected_part: e.target.value }))} data-testid="maint-ts-form-part" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Troubleshooting details *</Label>
                <Textarea className="mt-1 text-sm bg-white" rows={3} placeholder="What was checked, findings, next steps…" value={form.details} onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))} data-testid="maint-ts-form-details" />
              </div>
              <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="maint-ts-form-save">Save entry</Button>
            </div>
          )}
          <div className="space-y-2" data-testid="maint-ts-list">
            {list === null && <p className="text-sm text-gray-400">Loading…</p>}
            {list !== null && filtered.length === 0 && (
              <p className="text-sm text-gray-400" data-testid="maint-ts-empty">
                {q ? "No entries match the search." : "No troubleshooting logged for this aircraft yet."}
              </p>
            )}
            {pageItems.map((t) => (
              <div key={t.id} className="border border-summit-hairline rounded-md px-3 py-2" data-testid={`maint-ts-entry-${t.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 font-mono text-[10px] uppercase tracking-widest">ATA {t.ata_chapter}</span>
                  {t.jobcard && <span className="font-mono text-[10px] text-gray-500">JC# {t.jobcard}</span>}
                  {t.affected_part && <span className="font-mono text-[10px] text-gray-500">Part: {t.affected_part}</span>}
                  <span className="ml-auto font-mono text-[10px] text-gray-400">{t.by_name} · {fmtWhen(t.created_at)}</span>
                  {(user.role === "admin" || t.by_id === user.id) && (
                    <button className="p-0.5 rounded text-gray-300 hover:text-rose-600" onClick={() => del(t)} data-testid={`maint-ts-del-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm mt-1 whitespace-pre-wrap text-gray-700">{t.details}</p>
                {(t.updates || []).map((u) => (
                  <div key={u.id} className="mt-1.5 ml-3 pl-2 border-l-2 border-summit-hairline" data-testid={`maint-ts-update-${u.id}`}>
                    <p className="text-xs whitespace-pre-wrap text-gray-600">{u.text}</p>
                    <span className="font-mono text-[10px] text-gray-400">{u.by_name} · {fmtWhen(u.at)}</span>
                  </div>
                ))}
                {updateFor === t.id ? (
                  <div className="mt-2 flex gap-2 items-start">
                    <Textarea className="text-sm flex-1" rows={2} placeholder="Add more details / findings…" value={updateText} onChange={(e) => setUpdateText(e.target.value)} data-testid={`maint-ts-update-text-${t.id}`} />
                    <div className="flex flex-col gap-1">
                      <Button size="sm" className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => addUpdate(t.id)} disabled={busy} data-testid={`maint-ts-update-save-${t.id}`}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setUpdateFor(null); setUpdateText(""); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <button className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-summit-primary hover:underline" onClick={() => { setUpdateFor(t.id); setUpdateText(""); }} data-testid={`maint-ts-addupdate-${t.id}`}>
                    <Plus className="w-3 h-3" /> Add details
                  </button>
                )}
              </div>
            ))}
            {filtered.length > PER_PAGE && (
              <div className="flex items-center justify-between pt-1" data-testid="maint-ts-pagination">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="maint-ts-page-prev">
                  <ChevronRight className="w-3 h-3 rotate-180 mr-1" /> Prev
                </Button>
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="maint-ts-page-info">
                  Page {curPage} of {totalPages} · {filtered.length} entries
                </span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="maint-ts-page-next">
                  Next <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ClockButton({ ac, active, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [capexOpen, setCapexOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  const [capexForm, setCapexForm] = useState({ code: "", note: "" });
  const mine = active && active.aircraft_tail === ac.tail;
  const needsCapex = ac.status === "heavy_check" || ac.status === "capex";

  const start = async (capexCode, capexNote) => {
    setBusy(true);
    try {
      let gps;
      try { gps = await getGps(); } catch { toast.error("Turn on location to clock in"); return; }
      const payload = { aircraft_tail: ac.tail, task_type: "jobcard-task", client_latitude: gps.lat, client_longitude: gps.lng };
      if (capexCode) { payload.capex_code = capexCode; payload.capex_note = capexNote; }
      await api.post("/clock/start", payload);
      toast.success(`Clocked in on ${ac.tail}`);
      setCapexOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clock in");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      let gps = null;
      try { gps = await getGps(); } catch {}
      await api.post("/clock/stop", gps
        ? { client_latitude: gps.lat, client_longitude: gps.lng }
        : { geofence_bypass_reason: "unavailable" });
      toast.success(`Clocked out of ${ac.tail} — added to your timesheet`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clock out");
    } finally {
      setBusy(false);
    }
  };

  const onClick = () => {
    if (busy) return;
    if (mine) { stop(); return; }
    if (active) { toast.error(`You're already clocked in on ${active.aircraft_tail} — stop that timer first.`); return; }
    if (needsCapex) {
      api.get(`/capex-codes?tail=${encodeURIComponent(ac.tail)}`).then((r) => setCodes(r.data || [])).catch(() => setCodes([]));
      setCapexForm({ code: "", note: "" });
      setCapexOpen(true);
      return;
    }
    start();
  };

  return (
    <>
      <button
        className={`p-1.5 rounded transition-colors ${mine ? "bg-rose-500/90 hover:bg-rose-500 animate-pulse" : "hover:bg-white/15"}`}
        onClick={onClick}
        disabled={busy}
        title={mine ? `Clock out — started ${fmtWhen(active.started_at)}` : `Clock in on ${ac.tail}`}
        data-testid={`maint-clock-btn-${ac.tail}`}
      >
        {mine ? <Square className="w-3.5 h-3.5" fill="currentColor" /> : <Clock className="w-3.5 h-3.5" />}
      </button>
      <Dialog open={capexOpen} onOpenChange={setCapexOpen}>
        <DialogContent className="max-w-md" data-testid="maint-clock-capex-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Clock className="w-4 h-4" /> Clock in on {ac.tail}</DialogTitle>
            <DialogDescription>{ac.tail} is on {(AC_STATUS[ac.status] || {}).label || ac.status} — a CAPEX Account Code is required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">CAPEX Account Code *</Label>
              <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={capexForm.code} onChange={(e) => setCapexForm((f) => ({ ...f, code: e.target.value }))} data-testid="maint-clock-capex-code">
                <option value="">Select a code…</option>
                {codes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">CAPEX note *</Label>
              <Textarea className="mt-1 text-sm" rows={2} placeholder="Describe the work…" value={capexForm.note} onChange={(e) => setCapexForm((f) => ({ ...f, note: e.target.value }))} data-testid="maint-clock-capex-note" />
            </div>
            <Button
              className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white"
              disabled={busy}
              onClick={() => {
                if (!capexForm.code) { toast.error("Pick a CAPEX Account Code"); return; }
                if (!capexForm.note.trim()) { toast.error("A CAPEX note is required"); return; }
                start(capexForm.code, capexForm.note.trim());
              }}
              data-testid="maint-clock-capex-start"
            >
              Clock in
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DailyInspectionsCard({ ac }) {
  const [list, setList] = useState([]);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/maintenance/aircraft/${encodeURIComponent(ac.tail)}/daily-inspections`)
      .then((r) => { if (alive) setList(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ac]);

  const downloadPdf = async (insp) => {
    setDownloading(insp.id);
    try {
      const { getToken } = await import("@/lib/api");
      const resp = await fetch(`${API_BASE}/maintenance/daily-inspections/${insp.id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Failed to open PDF"); }
    finally { setDownloading(null); }
  };

  if (!["ATR72", "DORNIER 228", "AVRO RJ85", "AVRO RJ100", "DASH 8"].includes((ac.fleet || "").toUpperCase())) return null;
  return (
    <div className="mt-5">
      <ColumnHead>Daily Inspection (48 h)</ColumnHead>
      <div className="space-y-1.5" data-testid={`maint-di-list-${ac.tail}`}>
        {list.length === 0 && <p className="text-xs text-gray-400">No inspections in the last 48 hours. Techs start them from the aircraft QR page.</p>}
        {list.map((i) => (
          <div key={i.id} className={`border rounded-md px-2.5 py-2 text-xs flex items-center gap-2 flex-wrap ${i.status === "complete" ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`} data-testid={`maint-di-${i.id}`}>
            <span className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 ${i.status === "complete" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {i.status === "complete" ? "Complete" : `${i.signed_count}/${i.total}`}
            </span>
            <span className="font-medium">{i.date || "—"}</span>
            <span className="text-gray-500">
              {i.status === "complete" ? `by ${i.completed_by_name} · ${fmtWhen(i.completed_at)}` : `started by ${i.created_by_name} · ${fmtWhen(i.created_at)}`}
            </span>
            <button
              className="ml-auto inline-flex items-center gap-1 text-summit-primary hover:underline shrink-0"
              onClick={() => downloadPdf(i)}
              disabled={downloading === i.id}
              data-testid={`maint-di-pdf-${i.id}`}
            >
              <Paperclip className="w-3 h-3" /> {downloading === i.id ? "Opening…" : "View / print PDF"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FlightScheduleDialog({ tail, open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open) {
      setData(null);
      api.get(`/fleet/flight-schedule/${encodeURIComponent(tail)}`).then((r) => setData(r.data)).catch(() => setData({ available: false }));
    }
  }, [open, tail]);
  const Legs = ({ legs, emptyText }) =>
    (legs || []).length === 0 ? (
      <p className="text-sm text-gray-400">{emptyText}</p>
    ) : (
      <div className="divide-y divide-summit-hairline/70">
        {legs.map((l, i) => (
          <div key={i} className="py-1.5 flex items-center gap-3 text-sm" data-testid={`sched-leg-${tail}-${i}`}>
            <span className="font-mono text-xs text-gray-400 w-5">{i + 1}.</span>
            <span className="font-mono font-semibold">{l.from} → {l.to}</span>
            <span className="ml-auto font-mono text-xs">
              <span className="text-amber-700">dep {l.dep || "—"}</span> · <span className="text-emerald-700">arr {l.arr || "—"}</span>
            </span>
          </div>
        ))}
      </div>
    );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid={`sched-dialog-${tail}`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PlaneTakeoff className="w-4 h-4 text-summit-primary" /> Flight schedule — {tail}
          </DialogTitle>
          <DialogDescription>
            {data ? (data.received_at ? `Last updated ${fmtWhen(data.received_at)}${data.source === "email" ? " (via email)" : ""}` : "No schedule on file yet.") : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Today · {data.today}</div>
              <div className="mt-1"><Legs legs={data.today_legs} emptyText="No flights today." /></div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Tomorrow · {data.tomorrow}</div>
              <div className="mt-1"><Legs legs={data.tomorrow_legs} emptyText="No flights tomorrow." /></div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AircraftPanel({ ac, orders, members, user, onSaved, onStatus, onQuickEdit, activeClock, onClockChanged, sched, schedMeta }) {
  const openCount = orders.filter((o) => o.status === "open").length;
  const openProblems = (ac.problem_reports || []).filter((p) => p.status === "open").length;
  const st = AC_STATUS[ac.status] || AC_STATUS.normal;
  const calOverdue = isOverdue(ac.next_calendar_due);
  const [schedOpen, setSchedOpen] = useState(false);
  const chipCls = "font-mono text-[11px] opacity-90 hover:opacity-100 hover:underline underline-offset-2 decoration-white/50";
  return (
    <div className="border border-summit-hairline rounded-lg overflow-hidden bg-white" data-testid={`maint-panel-${ac.tail}`}>
      <div className="bg-summit-primary text-white px-4 py-2 flex items-center gap-3 flex-wrap">
        <Plane className={`w-4 h-4 shrink-0 ${st.plane}`} fill="currentColor" />
        <span className="font-display font-semibold">{ac.tail}</span>
        {ac.fleet && <span className="font-mono text-[11px] uppercase tracking-widest opacity-80">— {ac.fleet}</span>}
        <button className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest cursor-pointer hover:ring-2 hover:ring-white/40 transition-shadow ${st.cls}`} onClick={onStatus} title="Click to update status" data-testid={`maint-status-badge-${ac.tail}`}>{st.label}</button>
        {ac.status === "unserviceable" && ac.us_return_to_service && (
          <span className="font-mono text-[10px] text-red-200" title="Anticipated return to service" data-testid={`maint-us-rts-${ac.tail}`}>
            RTS {ac.us_return_to_service.replace("T", " ")}
          </span>
        )}
        {ac.status_note && ac.status !== "unserviceable" && <span className="font-mono text-[10px] opacity-80 truncate max-w-[200px]" title={ac.status_note}>“{ac.status_note}”</span>}
        <button className={chipCls} onClick={() => onQuickEdit("log_number")} title="Click to update Log #" data-testid={`maint-qe-log-${ac.tail}`}>
          Log# {ac.log_number ?? "—"}
        </button>
        <button className={chipCls} onClick={() => onQuickEdit("airframe_hours")} title="Click to update aircraft hours" data-testid={`maint-qe-hours-${ac.tail}`}>
          {ac.airframe_hours ?? "—"} <span className="opacity-60 text-[9px] uppercase">hrs</span>
        </button>
        <button className={chipCls} onClick={() => onQuickEdit("cycles")} title="Click to update cycles" data-testid={`maint-qe-cycles-${ac.tail}`}>
          {ac.cycles ?? "—"} <span className="opacity-60 text-[9px] uppercase">cycles</span>
        </button>
        <button className={`${chipCls} ${calOverdue ? "text-rose-300 font-bold" : ""}`} onClick={() => onQuickEdit("next_calendar_due")} title="Click to update next calendar due" data-testid={`maint-qe-caldue-${ac.tail}`}>
          {ac.next_calendar_due || "—"} <span className="opacity-60 text-[9px] uppercase">next due</span>{calOverdue ? " · OVERDUE" : ""}
        </button>
        <button className={chipCls} onClick={() => onQuickEdit("base")} title="Click to set the aircraft's base" data-testid={`maint-qe-base-${ac.tail}`}>
          {ac.base || "—"} <span className="opacity-60 text-[9px] uppercase">base</span>
        </button>
        {sched && (
          <>
          <button type="button" className="ml-auto font-mono text-[10px] inline-flex items-center gap-1.5 hover:underline underline-offset-2 decoration-white/50" onClick={() => setSchedOpen(true)} title={`Click for the full today + tomorrow schedule — last updated ${fmtWhen(schedMeta?.received_at)}${schedMeta?.source === "email" ? " (via email)" : ""}`} data-testid={`maint-sched-${ac.tail}`}>
            <PlaneTakeoff className="w-3 h-3 opacity-70" />
            {sched.today_last ? (
              <>
                <span className="opacity-80">Last today {sched.today_last.from}→{sched.today_last.to}</span>
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-400 text-emerald-950 font-bold uppercase tracking-wide" data-testid={`maint-sched-arr-${ac.tail}`}>arr {sched.today_last.arr}</span>
              </>
            ) : (
              <span className="opacity-70">No flights today</span>
            )}
            <span className="opacity-40">·</span>
            {sched.tomorrow_first ? (
              <>
                <span className="px-1.5 py-0.5 rounded-full bg-amber-300 text-amber-950 font-bold uppercase tracking-wide" data-testid={`maint-sched-dep-${ac.tail}`}>tmrw dep {sched.tomorrow_first.dep}</span>
                <span className="opacity-80">{sched.tomorrow_first.from}→{sched.tomorrow_first.to}</span>
              </>
            ) : (
              <span className="opacity-70">None tmrw</span>
            )}
          </button>
          <FlightScheduleDialog tail={ac.tail} open={schedOpen} onClose={() => setSchedOpen(false)} />
          </>
        )}
        <span className="ml-auto flex items-center gap-2">
          {openCount > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/15 font-mono text-[10px]" data-testid={`maint-wo-count-${ac.tail}`}>
              <ClipboardList className="w-3 h-3" /> {openCount} open
            </span>
          )}
          {openProblems > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500/90 font-mono text-[10px]" data-testid={`maint-pr-count-${ac.tail}`}>
              <AlertTriangle className="w-3 h-3" /> {openProblems} PR
            </span>
          )}
          <TroubleshootButton ac={ac} user={user} />
          <ClockButton ac={ac} active={activeClock} onChanged={onClockChanged} />
          <button className="p-1.5 rounded hover:bg-white/15 transition-colors" onClick={() => printAircraftQr(ac)} title="Print QR code (includes PIN)" data-testid={`maint-qr-btn-${ac.tail}`}>
            <QrCode className="w-3.5 h-3.5" />
          </button>
          <button className="p-1.5 rounded hover:bg-white/15 transition-colors" onClick={onStatus} title="Update status" data-testid={`maint-edit-${ac.tail}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5 p-4 bg-summit-canvas/30">
        <div>
          <MelCard ac={ac} onSaved={onSaved} />
          <ProblemReports ac={ac} user={user} onSaved={onSaved} />
          <DailyInspectionsCard ac={ac} />
        </div>
        <WorkOrders ac={ac} orders={orders} members={members} user={user} onSaved={onSaved} />
        <WorkLog ac={ac} onSaved={onSaved} />
      </div>
    </div>
  );
}

export default function Maintenance() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [orders, setOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedTail, setSelectedTail] = useState(() => new URLSearchParams(window.location.search).get("tail") || "all");
  const [query, setQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [statusTail, setStatusTail] = useState(null);
  const [quickEdit, setQuickEdit] = useState(null); // {tail, field}
  const [activeClock, setActiveClock] = useState(null);
  const [favs, setFavs] = useState([]);
  const [sched, setSched] = useState(null);

  useEffect(() => { if (user) setFavs(user.favourite_aircraft || []); }, [user]);

  const toggleFav = async (tail, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const r = favs.includes(tail)
        ? await api.delete(`/users/me/favourites/${tail}`)
        : await api.post(`/users/me/favourites/${tail}`);
      setFavs(r.data.favourite_aircraft || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update favourites");
    }
  };

  const loadClock = async () => {
    try {
      const r = await api.get("/clock/active");
      setActiveClock(r.data || null);
    } catch {}
  };

  const load = async () => {
    try {
      const [a, b, c, s] = await Promise.all([
        api.get("/maintenance/aircraft"),
        api.get("/maintenance/work-orders"),
        api.get("/maintenance/members"),
        api.get("/fleet/flight-schedule/summary").catch(() => ({ data: null })),
      ]);
      setItems(a.data || []);
      setOrders(b.data || []);
      setMembers(c.data || []);
      setSched(s.data?.available ? s.data : null);
    } catch (e) {
      setItems([]);
      toast.error(e?.response?.data?.detail || "Failed to load maintenance data");
    }
  };
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => { if (user) { load(); loadClock(); } }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live updates — WebSocket ping on any maintenance mutation, with
  // auto-reconnect and a slow polling fallback.
  useEffect(() => {
    if (!user) return undefined;
    let ws = null;
    let retry = null;
    let debounce = null;
    let closed = false;
    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(`${API_BASE.replace(/^http/, "ws")}/ws/maintenance`);
      } catch {
        retry = setTimeout(connect, 5000);
        return;
      }
      ws.onmessage = () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => loadRef.current(), 250);
      };
      ws.onclose = () => { if (!closed) retry = setTimeout(connect, 3000); };
    };
    connect();
    const poll = setInterval(() => loadRef.current(), 60000);
    return () => {
      closed = true;
      clearTimeout(retry);
      clearTimeout(debounce);
      clearInterval(poll);
      try { ws && ws.close(); } catch {}
    };
  }, [user]);

  const tiles = useMemo(() => {
    const all = items || [];
    return {
      total: all.length,
      normal: all.filter((i) => i.status === "normal").length,
      heavy_check: all.filter((i) => i.status === "heavy_check").length,
      mel: all.filter((i) => i.status === "mel").length,
      capex: all.filter((i) => i.status === "capex").length,
      unserviceable: all.filter((i) => i.status === "unserviceable").length,
      openWo: orders.filter((o) => o.status === "open").length,
      openPr: all.reduce((n, i) => n + (i.problem_reports || []).filter((p) => p.status === "open").length, 0),
    };
  }, [items, orders]);

  const sideList = useMemo(() => {
    let list = items || [];
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((i) => [i.tail, i.fleet].some((v) => (v || "").toLowerCase().includes(q)));
    const userBase = (user?.city || "").toUpperCase();
    const rank = (i) => (i.status === "offline" ? 3 : favs.includes(i.tail) ? 0 : userBase && (i.base || "").toUpperCase() === userBase ? 1 : 2);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [items, query, favs, user]);

  const panels = useMemo(() => {
    if (selectedTail === "all") return sideList;
    const one = (items || []).find((i) => i.tail === selectedTail);
    return one ? [one] : [];
  }, [items, sideList, selectedTail]);

  if (!user) return null;

  const STAT_CHIPS = [
    ["normal", "serviceable", "text-emerald-700"],
    ["heavy_check", "heavy check", "text-blue-700"],
    ["mel", "MEL", "text-amber-700"],
    ["unserviceable", "unserviceable", "text-red-700"],
  ];

  return (
    <div className="flex" style={{ minHeight: "calc(100vh - 56px)" }}>
      {/* Side nav — aircraft list (flat, from Fleet Status) */}
      <aside className={`hidden md:flex flex-col shrink-0 bg-white border-r border-summit-hairline ${sidebarOpen ? "w-44 lg:w-52 xl:w-60" : "w-10"}`} data-testid="maint-sidebar">
        {!sidebarOpen ? (
          <button className="p-2.5 flex justify-center hover:bg-summit-glacier/40" onClick={() => setSidebarOpen(true)} title="Show aircraft list" data-testid="maint-sidebar-expand">
            <PanelLeftOpen className="w-4 h-4 text-gray-500" />
          </button>
        ) : (
        <>
        <div className="p-3 border-b border-summit-hairline">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center justify-between">
            <span>Aircraft</span>
            <button className="p-0.5 rounded hover:bg-summit-glacier/60 text-gray-400 hover:text-gray-600" onClick={() => setSidebarOpen(false)} title="Hide aircraft list" data-testid="maint-sidebar-collapse">
              <PanelLeftClose className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="h-8 pl-8 text-xs" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="maint-search" />
            {query && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setQuery("")} data-testid="maint-search-clear"><X className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <button
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-l-2 ${selectedTail === "all" ? "border-summit-primary bg-summit-glacier/60 font-medium" : "border-transparent hover:bg-summit-glacier/40"}`}
            onClick={() => setSelectedTail("all")}
            data-testid="maint-side-all"
          >
            <LayoutList className="w-3.5 h-3.5 text-gray-400" /> All Aircraft
            <span className="ml-auto font-mono text-[10px] text-gray-400">{(items || []).length}</span>
          </button>
          {sideList.map((i) => {
            const hasPr = (i.problem_reports || []).some((p) => p.status === "open");
            return (
              <button
                key={i.tail}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-l-2 ${selectedTail === i.tail ? "border-summit-primary bg-summit-glacier/60 font-medium" : "border-transparent hover:bg-summit-glacier/40"}`}
                onClick={() => setSelectedTail(i.tail)}
                data-testid={`maint-side-item-${i.tail}`}
              >
                <Plane className={`w-3.5 h-3.5 shrink-0 ${(AC_STATUS[i.status] || AC_STATUS.normal).plane}`} fill="currentColor" data-testid={`maint-side-plane-${i.tail}`} />
                <span className="truncate">{i.tail}</span>
                <span className="ml-auto flex items-center gap-1 shrink-0">
                  {hasPr && <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" title="Open problem" data-testid={`maint-side-pr-${i.tail}`} />}
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => toggleFav(i.tail, e)}
                    title={favs.includes(i.tail) ? "Remove favourite" : "Pin as favourite"}
                    data-testid={`maint-fav-${i.tail}`}
                  >
                    <Star className={`w-3.5 h-3.5 transition-colors ${favs.includes(i.tail) ? "text-amber-400 fill-amber-400" : "text-gray-300 hover:text-amber-400"}`} />
                  </span>
                </span>
              </button>
            );
          })}
          {items !== null && sideList.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No matches.</p>}
        </div>
        </>
        )}
      </aside>

      {/* Right — working area */}
      <main className="flex-1 min-w-0 px-4 sm:px-6 py-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight" data-testid="maint-heading">Aircraft Maintenance</h1>
            <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap" data-testid="maint-tiles">
              {STAT_CHIPS.map(([k, label, cls]) => (
                <span key={k} className={`font-mono text-[11px] ${cls}`}>
                  <span className="font-bold" data-testid={`maint-tile-${k}`}>{tiles[k]}</span> {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile aircraft picker (sidebar hidden < md) */}
        <select
          className="md:hidden mt-3 h-9 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm"
          value={selectedTail}
          onChange={(e) => setSelectedTail(e.target.value)}
          data-testid="maint-mobile-select"
        >
          <option value="all">All Aircraft</option>
          {sideList.map((i) => <option key={i.tail} value={i.tail}>{i.tail}</option>)}
        </select>

        <div className="mt-4 space-y-5">
          {items === null ? (
            <div className="text-center text-gray-400 text-sm py-12">Loading…</div>
          ) : panels.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-12 border border-dashed border-summit-hairline rounded-lg" data-testid="maint-empty">
              {(items || []).length === 0 ? "No active aircraft in the fleet." : "Nothing matches the current search."}
            </div>
          ) : selectedTail === "all" ? (
            <div className="border border-summit-hairline rounded-lg overflow-hidden bg-white" data-testid="maint-summary">
              <div className="bg-summit-primary text-white px-4 py-2 flex items-center gap-3">
                <Plane className="w-4 h-4 opacity-80" />
                <span className="font-display font-semibold">Fleet</span>
                <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">{sideList.length} aircraft</span>
              </div>
              <div className="grid grid-cols-[minmax(0,1fr)_72px_72px_80px] sm:grid-cols-[minmax(0,1fr)_100px_100px_100px] px-4 py-1.5 border-b border-summit-hairline font-mono text-[9px] uppercase tracking-widest text-gray-400">
                <span>Tail</span><span className="text-right">Alloc. hrs</span><span className="text-right">Pending</span><span className="text-right">Completed</span>
              </div>
              {sideList.map((i) => {
                const hours = Math.round((i.work_log || []).filter((l) => !l.archived).reduce((n, l) => n + (Number(l.labor_hours) || 0), 0) * 10) / 10;
                const pending = orders.filter((o) => o.tail === i.tail && o.status === "open").length;
                const completed = (i.work_log || []).length;
                return (
                  <button
                    key={i.tail}
                    className="w-full grid grid-cols-[minmax(0,1fr)_72px_72px_80px] sm:grid-cols-[minmax(0,1fr)_100px_100px_100px] items-center px-4 py-2 text-sm border-b border-summit-hairline last:border-b-0 hover:bg-summit-glacier/40 text-left transition-colors"
                    onClick={() => setSelectedTail(i.tail)}
                    data-testid={`maint-summary-row-${i.tail}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Plane className={`w-3 h-3 shrink-0 ${(AC_STATUS[i.status] || AC_STATUS.normal).plane}`} fill="currentColor" />
                      <span className="truncate font-medium">{i.tail}</span>
                      <span className="font-mono text-[10px] text-gray-400 truncate">{i.fleet}</span>
                      {(i.mel_items || []).length > 0 && (
                        <span className="font-mono text-[9px] uppercase tracking-widest text-amber-700 bg-amber-50 px-1.5 rounded shrink-0">MEL {(i.mel_items || []).length}</span>
                      )}
                      {(i.problem_reports || []).some((p) => p.status === "open") && (
                        <span className="font-mono text-[9px] uppercase tracking-widest text-rose-700 bg-rose-50 px-1.5 rounded shrink-0">PR</span>
                      )}
                    </span>
                    <span className="text-right font-mono text-xs">{hours || "—"}</span>
                    <span className={`text-right font-mono text-xs ${pending > 0 ? "text-amber-700 font-bold" : "text-gray-400"}`}>{pending}</span>
                    <span className="text-right font-mono text-xs text-gray-600">{completed}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            panels.map((ac) => (
              <AircraftPanel
                key={ac.tail}
                ac={ac}
                orders={orders.filter((o) => o.tail === ac.tail)}
                members={members}
                user={user}
                onSaved={load}
                onStatus={() => setStatusTail(ac.tail)}
                onQuickEdit={(field) => setQuickEdit({ tail: ac.tail, field })}
                activeClock={activeClock}
                onClockChanged={() => { loadClock(); load(); }}
                sched={sched?.by_tail?.[ac.tail]}
                schedMeta={sched}
              />
            ))
          )}
        </div>
      </main>

      <StatusDialog
        ac={(items || []).find((i) => i.tail === statusTail) || null}
        open={!!statusTail}
        onOpenChange={(o) => { if (!o) setStatusTail(null); }}
        onSaved={load}
      />
      {quickEdit && (
        <QuickEditDialog
          ac={(items || []).find((i) => i.tail === quickEdit.tail) || null}
          field={quickEdit.field}
          onClose={() => setQuickEdit(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
